<script>
import { useAlert } from 'dashboard/composables';
import DatePicker from 'vue2-datepicker';
import { format, addDays, parseISO } from 'date-fns';
import { FIELD_CLASS, SELECT_CLASS } from '../constants';
import { apiErrorMessage } from '../../../../api/realEstate/errors';
import {
  getAvailableDates,
  getAvailableSlots,
} from '../../../../api/realEstate/availability';

const DATE_SPAN_DAYS = 30;

// Shared by the New/Reschedule appointment dialogs — everything about
// "when can this be booked" (docs/real-estate/be/10-api-reference.md
// §A12). Walk-in viewings pick a date + an arrival time inside one of the
// day's `arrival_windows`; agent-mode viewings and key handovers pick a
// date + a discrete slot (optionally narrowed to one agent), stepping by
// the appointment's duration. The parent owns everything about the
// appointment itself (customer, notes, ...) — this component only ever
// emits the when/who-books-it part.
export default {
  name: 'AvailabilityPicker',
  components: { DatePicker },
  props: {
    propertyId: {
      type: String,
      required: true,
    },
    appointmentType: {
      type: String,
      required: true,
    },
    bookingMode: {
      type: String,
      required: true,
    },
    // Required for a key handover (server rejects a viewing that sends
    // one at all) — the project's own duration is used for a viewing.
    durationMinutes: {
      type: Number,
      default: null,
    },
    agents: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      fieldClass: FIELD_CLASS,
      selectClass: SELECT_CLASS,
      lang: {
        days: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
        yearFormat: 'YYYY',
        monthFormat: 'MMMM',
      },
      selectedDate: null,
      availableDates: null,
      unavailableDates: [],
      isLoadingDates: false,
      slotsResponse: null,
      isLoadingSlots: false,
      arrivalTime: '',
      selectedSlot: null,
      agentId: '',
    };
  },
  computed: {
    // A key handover is always booked into an agent slot, even at a walk-in
    // project — the server answers its availability in slot form
    // (`arrival_windows: null`), so only a walk-in *viewing* gets the
    // arrival-time input.
    isSlotMode() {
      return (
        this.bookingMode === 'agent' || this.appointmentType === 'key_handover'
      );
    },
    // A handover's slots depend on its duration, which the parent asks for
    // first — there's nothing to look up (the server would 422) until then.
    isWaitingForDuration() {
      return this.appointmentType === 'key_handover' && !this.durationMinutes;
    },
    // Why dates in the visible range can't be booked, grouped by reason.
    // Past dates are left out — greyed days before today need no
    // explanation, and they'd bury the useful ones.
    unavailableSummary() {
      const byReason = [];
      this.unavailableDates
        .filter(item => item.reason && item.reason !== 'past_date')
        .forEach(item => {
          let group = byReason.find(g => g.reason === item.reason);
          if (!group) {
            group = { reason: item.reason, days: [] };
            byReason.push(group);
          }
          group.days.push(format(parseISO(item.date), 'd MMM'));
        });
      return byReason;
    },
    // Every bookable day is gone for one shared reason (e.g. no agent is
    // assigned at all): say it once instead of listing each date.
    allUnavailableReason() {
      if (!this.availableDates || this.availableDates.length) return null;
      return this.unavailableSummary.length === 1
        ? this.unavailableSummary[0].reason
        : null;
    },
    selection() {
      if (!this.selectedDate) return null;
      const date = format(this.selectedDate, 'yyyy-MM-dd');
      if (!this.isSlotMode) {
        if (!this.arrivalTime) return null;
        return { date, start_time: this.arrivalTime };
      }
      if (!this.selectedSlot) return null;
      const agentFields = this.agentId
        ? { agent_id: this.agentId }
        : { auto_assign_agent: true };
      return { date, start_time: this.selectedSlot.start_time, ...agentFields };
    },
  },
  watch: {
    selection: {
      immediate: true,
      handler(value) {
        this.$emit('change', value);
      },
    },
    propertyId() {
      this.reset();
    },
    appointmentType() {
      this.reset();
    },
    durationMinutes() {
      this.reset();
    },
  },
  mounted() {
    this.fetchAvailableDates(new Date());
  },
  methods: {
    reset() {
      this.selectedDate = null;
      this.availableDates = null;
      this.unavailableDates = [];
      this.slotsResponse = null;
      this.arrivalTime = '';
      this.selectedSlot = null;
      this.agentId = '';
      this.fetchAvailableDates(new Date());
    },
    availabilityOpts() {
      return this.appointmentType === 'key_handover'
        ? {
            appointment_type: this.appointmentType,
            duration_minutes: this.durationMinutes,
          }
        : { appointment_type: this.appointmentType };
    },
    async fetchAvailableDates(fromDate) {
      if (this.isWaitingForDuration) {
        this.availableDates = null;
        this.unavailableDates = [];
        return;
      }
      this.isLoadingDates = true;
      try {
        const { data } = await getAvailableDates(this.propertyId, {
          fromDate: format(fromDate, 'yyyy-MM-dd'),
          toDate: format(addDays(fromDate, DATE_SPAN_DAYS), 'yyyy-MM-dd'),
          ...this.availabilityOpts(),
        });
        this.availableDates = data.available_dates;
        this.unavailableDates = data.unavailable_dates || [];
      } catch (err) {
        useAlert(apiErrorMessage(this, err));
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        this.isLoadingDates = false;
      }
    },
    handleMonthChange(newDate, oldDate, type) {
      if (type !== 'next-month' && type !== 'last-month') return;
      this.fetchAvailableDates(newDate);
    },
    disableDate(date) {
      if (!this.availableDates) return true;
      return !this.availableDates.includes(format(date, 'yyyy-MM-dd'));
    },
    async handleDateSelect(date) {
      this.selectedDate = date;
      this.arrivalTime = '';
      this.selectedSlot = null;
      this.isLoadingSlots = true;
      this.slotsResponse = null;
      try {
        const { data } = await getAvailableSlots(this.propertyId, {
          date: format(date, 'yyyy-MM-dd'),
          ...this.availabilityOpts(),
        });
        this.slotsResponse = data;
      } catch (err) {
        useAlert(apiErrorMessage(this, err));
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        this.isLoadingSlots = false;
      }
    },
    isSlotDisabled(slot) {
      return this.agentId
        ? !slot.available_agent_ids.includes(this.agentId)
        : false;
    },
    selectSlot(slot) {
      if (this.isSlotDisabled(slot)) return;
      this.selectedSlot = slot;
    },
  },
};
</script>

<template>
  <div>
    <DatePicker
      v-model="selectedDate"
      type="date"
      :lang="lang"
      inline
      :disabled-date="disableDate"
      format="yyyy-MM-dd"
      :popup-style="{ width: '100%' }"
      @change="handleDateSelect"
      @calendar-change="handleMonthChange"
    />

    <template v-if="!selectedDate && !isLoadingDates">
      <p
        v-if="allUnavailableReason"
        class="text-xs text-amber-600 dark:text-amber-400 mt-2"
      >
        {{
          $t(
            `REAL_ESTATE.UNAVAILABLE_REASON.${allUnavailableReason.toUpperCase()}`
          )
        }}
      </p>
      <template v-else>
        <p
          v-for="group in unavailableSummary"
          :key="group.reason"
          class="text-xs text-amber-600 dark:text-amber-400 mt-2"
        >
          {{
            $t(`REAL_ESTATE.UNAVAILABLE_REASON.${group.reason.toUpperCase()}`)
          }}: {{ group.days.join(', ') }}
        </p>
      </template>
    </template>

    <p v-if="isWaitingForDuration" class="text-xs text-slate-400 mt-2">
      {{ $t('REAL_ESTATE.AVAILABILITY.DURATION_FIRST') }}
    </p>

    <template v-if="selectedDate">
      <p v-if="isLoadingSlots" class="text-xs text-slate-400 mt-2">
        {{ $t('REAL_ESTATE.AVAILABILITY.LOADING') }}
      </p>

      <template v-else-if="slotsResponse">
        <p
          v-if="slotsResponse.unavailable_reason"
          class="text-xs text-amber-600 dark:text-amber-400 mt-2"
        >
          {{
            $t(
              `REAL_ESTATE.UNAVAILABLE_REASON.${slotsResponse.unavailable_reason.toUpperCase()}`
            )
          }}
        </p>

        <template v-else-if="!isSlotMode">
          <p class="text-xs text-slate-400 mt-2">
            {{
              $t('REAL_ESTATE.AVAILABILITY.ARRIVAL_WINDOWS', {
                windows: slotsResponse.arrival_windows
                  .map(w => `${w.start_time}–${w.end_time}`)
                  .join(', '),
              })
            }}
          </p>
          <label class="block mt-2">
            <span class="text-sm text-slate-700 dark:text-slate-200">
              {{ $t('REAL_ESTATE.AVAILABILITY.FIELD_ARRIVAL_TIME') }}
            </span>
            <input
              v-model="arrivalTime"
              type="time"
              :class="fieldClass"
              class="mt-1"
            />
          </label>
        </template>

        <template v-else>
          <label v-if="agents.length" class="block mt-2 mb-2">
            <span class="text-sm text-slate-700 dark:text-slate-200">
              {{ $t('REAL_ESTATE.AVAILABILITY.FIELD_AGENT') }}
            </span>
            <select v-model="agentId" :class="selectClass" class="mt-1">
              <option value="">
                {{ $t('REAL_ESTATE.AVAILABILITY.AUTO_ASSIGN') }}
              </option>
              <option
                v-for="agent in agents"
                :key="agent.agent_id"
                :value="agent.agent_id"
              >
                {{ agent.name }}
              </option>
            </select>
          </label>

          <p
            v-if="!slotsResponse.slots.length"
            class="text-xs text-slate-400 mt-2"
          >
            {{ $t('REAL_ESTATE.AVAILABILITY.NO_SLOTS') }}
          </p>
          <div v-else class="flex flex-wrap gap-2 mt-2">
            <button
              v-for="slot in slotsResponse.slots"
              :key="slot.start_time"
              type="button"
              class="px-3 py-1.5 rounded-md text-sm font-medium border transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              :class="
                selectedSlot && selectedSlot.start_time === slot.start_time
                  ? 'bg-woot-500 border-woot-500 text-white'
                  : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300'
              "
              :disabled="isSlotDisabled(slot)"
              @click="selectSlot(slot)"
            >
              {{ slot.start_time }}–{{ slot.end_time }}
            </button>
          </div>
        </template>
      </template>
    </template>
  </div>
</template>
